import React, { useState } from 'react';
import Select, { MultiValue } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { toast } from 'react-toastify';
import '../../styles/shared.css';
import '../../styles/formats.css';
import { SizeOption, AdSize, getAdpFormatIcon } from '../../constants/formatConstants';
import {
    createSizeOptions,
    getSizeSelectValue,
    convertSelectToSizes,
    generateAdUnitPath,
    filterValidSizes
} from '../../utils/formatUtils';
import { createOption } from '../../../../helpers/selectHelpers';
import AdpFormatsConfigModal from './AdpFormatsConfigModal';

interface FormatsTabProps {
    handleForm: any;
}

const AdpFormatsTab: React.FC<FormatsTabProps> = ({ handleForm }) => {
    const [showGlobalSettingsModal, setShowGlobalSettingsModal] = useState<boolean>(false);
    
    // Tamanhos padrão disponíveis
    const defaultSizes: AdSize[] = [
        [970, 250], [970, 210], [970, 90], [728, 90], [468, 60],
        [336, 280], [320, 480], [320, 250], [320, 100], [320, 50],
        [300, 600], [300, 250], [300, 100], [300, 50], [160, 600], [120, 600]
    ];
    
    // Estado para opções de tamanho (permite adicionar customizados)
    const [sizeOptions, setSizeOptions] = useState<SizeOption[]>(createSizeOptions(defaultSizes));

    // Obter placements ADP do backend (exceto custom) agrupados por nome
    const getAdpPlacements = () => {
        const placements = handleForm.values.placements || [];
        const adpPlacements = placements.filter((p: any) => p.provider === 'adp' && p.name !== 'custom');

        // Agrupar por nome
        const grouped = adpPlacements.reduce((acc: any, placement: any) => {
            if (!acc[placement.name]) {
                acc[placement.name] = [];
            }
            acc[placement.name].push(placement);
            return acc;
        }, {});

        return Object.keys(grouped).map(name => ({
            name,
            placements: grouped[name],
            // Pegar dados do primeiro placement para info geral do formato
            displayName: grouped[name][0]?.displayName || name,
            description: grouped[name][0]?.description || `Formato ${name}`,
            icon: getAdpFormatIcon(name)
        }));
    };

    // Obter placements ADP customizados
    const getCustomPlacements = () => {
        const placements = handleForm.values.placements || [];
        return placements.filter((p: any) => p.provider === 'adp' && p.name === 'custom');
    };

    const handleGenerateAdUnitPath = (formatName: string, position?: string) => {
        return generateAdUnitPath(
            'adp',
            formatName,
            position,
            handleForm.values.managerType,
            handleForm.values.networkId,
            handleForm.values.domain,
        );
    };

    // Atualizar placement no array
    const updatePlacementField = (placementId: string, field: string, value: any) => {
        const updatedPlacements = (handleForm.values.placements || []).map((p: any) => {
            // Verificar por ID direto ou construir ID baseado em name-position
            const currentId = p.id || `${p.name}-${p.position || 'default'}`;
            if (
                currentId === placementId ||
                (p.name === placementId && p.provider === 'adp') ||
                (p.name === 'custom' && p.displayName === placementId && p.provider === 'adp')
            ) {
                return { ...p, [field]: value };
            }
            return p;
        });
        handleForm.setFieldValue('placements', updatedPlacements);
    };

    // Toggle habilitado/desabilitado para um placement específico
    const togglePlacementEnabled = (placementId: string) => {
        const placement = (handleForm.values.placements || []).find((p: any) => {
            const currentId = p.id || `${p.name}-${p.position || 'default'}`;
            return currentId === placementId || (p.name === placementId && p.provider === 'adp');
        });
        if (placement) {
            const currentId = placement.id || `${placement.name}-${placement.position || 'default'}`;
            updatePlacementField(currentId, 'enabled', !placement.enabled);
        }
    };

    // Atualizar tamanhos
    const updatePlacementSizes = (placementId: string, selectedSizes: MultiValue<SizeOption>) => {
        const sizesArray = convertSelectToSizes(selectedSizes as SizeOption[]);
        const validSizes = filterValidSizes(sizesArray);
        updatePlacementField(placementId, 'sizes', validSizes);
    };

    // Adicionar formato customizado
    const addCustomFormat = () => {
        const currentPlacements = [...(handleForm.values.placements || [])];
        const customPlacementsCount = currentPlacements.filter(p => p.name === 'custom').length;
        const customId = `custom-${customPlacementsCount}`;
        currentPlacements.push({
            id: customId,
            name: 'custom',
            displayName: '',
            enabled: true,
            adUnitPath: handleGenerateAdUnitPath('custom'),
            selector: '',
            refresh: 30,
            provider: 'adp',
            sizes: [],
            attributes: '',
            injectorPosition: 4,
        });
        handleForm.setFieldValue('placements', currentPlacements);
    };

    // Remover formato customizado
    const removeCustomFormat = (placementId: string) => {
        const currentPlacements = (handleForm.values.placements || []).filter((p: any) =>
            !(p.id === placementId || (p.name === placementId && p.provider === 'adp'))
        );
        handleForm.setFieldValue('placements', currentPlacements);
    };

    // Renderizar input de tamanhos
    const renderSizesInput = (placement: any) => {
        const currentSizeValue = getSizeSelectValue(placement?.sizes || []);
        const fieldId = `sizes-${placement.id || `${placement.name}-${placement.position || 'default'}`}`;
        const placementId = placement.id || `${placement.name}-${placement.position || 'default'}`;

        return (
            <div className="form-group">
                <label htmlFor={fieldId} className="form-label">Tamanhos<span className="required">*</span></label>
                <CreatableSelect
                    id={fieldId}
                    name={fieldId}
                    isMulti
                    options={sizeOptions}
                    value={currentSizeValue}
                    onChange={(selected) => updatePlacementSizes(placementId, selected)}
                    onCreateOption={(inputValue) => {
                        // Validar formato: deve ser "larguraxaltura" (ex: 300x250) ou "fluid"
                        const isValid = inputValue === 'fluid' || /^\d+x\d+$/.test(inputValue);
                        if (!isValid) {
                            toast.error('Tamanho inválido. Use o formato {largura}x{altura}, ex: 300x250');
                            return;
                        }
                        
                        // Criar nova opção e adicionar ao estado
                        const newOption = createOption(`${inputValue}`, inputValue);
                        setSizeOptions(prev => {
                            // Evitar duplicatas
                            if (prev.find(o => o.value === inputValue)) {
                                return prev;
                            }
                            return [...prev, newOption];
                        });
                        
                        // Adicionar o novo tamanho ao placement
                        const [width, height] = inputValue.split('x').map(Number);
                        const newSizes = [...(placement?.sizes || []), [width, height]];
                        updatePlacementField(placementId, 'sizes', newSizes);
                    }}
                    placeholder="Selecione os tamanhos..."
                />
            </div>
        );
    };

    // Renderizar input de atributos
    const renderAttributesInput = (placement: any) => {
        const fieldId = `attributes-${placement.id || `${placement.name}-${placement.position || 'default'}`}`;
        return (
            <div className="form-group">
                <label htmlFor={fieldId} className="form-label">
                    Attributes (JSON)<span className="text-muted small d-block">Configurações adicionais em formato JSON</span>
                </label>
                <textarea
                    id={fieldId}
                    name={fieldId}
                    className="form-control"
                    rows={3}
                    placeholder="{}"
                    value={placement?.attributes || ''}
                    onChange={(e) => updatePlacementField(placement.id || `${placement.name}-${placement.position || 'default'}`, 'attributes', e.target.value)}
                />
            </div>
        );
    };

    // Renderizar input de bids
    const renderBidsInput = (placement: any) => {
        const fieldId = `bids-${placement.id || `${placement.name}-${placement.position || 'default'}`}`;
        return (
            <div className="form-group">
                <label htmlFor={fieldId} className="form-label">
                    Bids Configuration (JSON)<span className="text-muted small d-block">Configuração dos bids para Prebid</span>
                </label>
                <textarea
                    id={fieldId}
                    name={fieldId}
                    className="form-control json-textarea"
                    rows={6}
                    placeholder={`[
  {
    "bidder": "appnexus",
    "params": {
      "placementId": 13144370
    }
  },
  {
    "bidder": "rubicon",
    "params": {
      "accountId": "12345",
      "siteId": "67890",
      "zoneId": "13579"
    }
  }
]`}
                    value={placement?.bids || ''}
                    onChange={(e) => updatePlacementField(placement.id || `${placement.name}-${placement.position || 'default'}`, 'bids', e.target.value)}
                />
                <div className="json-hint">
                    💡 Formato JSON válido necessário
                </div>
            </div>
        );
    };

    // Renderizar campos de configuração
    const renderFormatConfig = (placement: any) => {
        if (!placement.enabled) return null;

        const baseId = placement.id || `${placement.name}-${placement.position}`;
        const showSelectorAndPosition = !['anchor', 'slider', 'corner'].includes(placement.name);

        return (
            <div className="format-config">
                <div className="config-divider"></div>
                <div className="row g-3">
                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label htmlFor={`adUnitPath-${baseId}`} className="form-label">
                                Ad Unit Path<span className="required">*</span>
                            </label>
                            <input
                                id={`adUnitPath-${baseId}`}
                                name={`adUnitPath-${baseId}`}
                                type="text"
                                className="form-control"
                                placeholder={handleGenerateAdUnitPath(placement.name, placement.position)}
                                value={placement?.adUnitPath || handleGenerateAdUnitPath(placement.name, placement.position)}
                                onChange={(e) => updatePlacementField(baseId, 'adUnitPath', e.target.value)}
                            />
                            <div className="field-hint">💡 Path gerado automaticamente baseado nas configurações</div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6">
                        <div className="form-group">
                            <label htmlFor={`refresh-${baseId}`} className="form-label">
                                Tempo de recarregamento (segundos)<span className="text-muted small d-block">0 = sem refresh</span>
                            </label>
                            <input
                                id={`refresh-${baseId}`}
                                name={`refresh-${baseId}`}
                                type="number"
                                className="form-control"
                                min="0"
                                value={placement?.refresh ?? 30}
                                onChange={(e) => updatePlacementField(baseId, 'refresh', parseInt(e.target.value) || 0)}
                            />
                        </div>
                    </div>

                    {showSelectorAndPosition && (
                        <>
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor={`selector-${baseId}`} className="form-label">
                                        Seletor CSS<span className="required">*</span>
                                    </label>
                                    <input
                                        id={`selector-${baseId}`}
                                        name={`selector-${baseId}`}
                                        type="text"
                                        className="form-control"
                                        placeholder="Exemplo: p, .article-content"
                                        value={placement?.selector || ''}
                                        onChange={(e) => updatePlacementField(baseId, 'selector', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor={`injectorPosition-${baseId}`} className="form-label">
                                        Posição de Injeção<span className="text-muted small d-block">Qual elemento será usado para inserir o anúncio</span>
                                    </label>
                                    <select
                                        id={`injectorPosition-${baseId}`}
                                        name={`injectorPosition-${baseId}`}
                                        className="form-control"
                                        value={placement?.injectorPosition || 1}
                                        onChange={(e) => updatePlacementField(baseId, 'injectorPosition', parseInt(e.target.value) || 1)}
                                    >
                                        <option value={1}>Antes do elemento (BEFORE)</option>
                                        <option value={2}>Primeiro filho (FIRST CHILD)</option>
                                        <option value={3}>Último filho (LAST CHILD)</option>
                                        <option value={4}>Depois do elemento (AFTER)</option>
                                    </select>
                                </div>
                            </div>


                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor={`all-${baseId}`} className="form-label">
                                        All<span className="text-muted small d-block">Aplica para todos os elementos encontrados?</span>
                                    </label>
                                    <select
                                        id={`all-${baseId}`}
                                        name={`all-${baseId}`}
                                        className="form-control"
                                        value={placement.all}
                                        onChange={e => {
                                            const value = e.target.value === 'true' ? true : e.target.value === 'false' ? false : null;
                                            updatePlacementField(baseId, 'all', value);
                                        }}
                                    >
                                        <option value="">Selecione...</option>
                                        <option value="true">Sim</option>
                                        <option value="false">Não</option>
                                    </select>
                                </div>
                            </div>

                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor={`index-${baseId}`} className="form-label">
                                        Index<span className="text-muted small d-block">Índice do elemento (opcional)</span>
                                    </label>
                                    <input
                                        id={`index-${baseId}`}
                                        name={`index-${baseId}`}
                                        type="number"
                                        className="form-control"
                                        value={placement?.index ?? ''}
                                        placeholder="Deixe em branco para não definir"
                                        onChange={e => {
                                            const val = e.target.value;
                                            updatePlacementField(baseId, 'index', val === '' ? null : Number(val));
                                        }}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {renderSizesInput(placement)}
                {renderAttributesInput(placement)}
                {/* Show bids configuration if prebid powerup is enabled */}
                {handleForm.values.powerUps?.find((pu: any) => pu.name === 'prebid')?.enabled && renderBidsInput(placement)}
            </div>
        );
    };

    // Renderizar configuração para formatos com posições
    const renderFormatWithPositions = (formatGroup: any) => {
        const hasMultiplePositions = formatGroup.placements.length > 1;
        const isAnyEnabled = formatGroup.placements.some((p: any) => p.enabled);

        return (
            <div
                key={formatGroup.name}
                className={`format-card ${isAnyEnabled ? 'enabled' : 'disabled'} ${hasMultiplePositions ? 'has-positions' : ''}`}
            >
                <div className="format-header">
                    <div className="format-info">
                        <div className="format-icon">{formatGroup.icon}</div>
                        <div className="format-details">
                            <h4 className="format-name">{formatGroup.displayName}</h4>
                            <p className="format-description">{formatGroup.description}</p>
                        </div>
                    </div>

                    {!hasMultiplePositions && (
                        <div className="format-toggle">
                            <label
                                className="toggle-switch"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <input
                                    type="checkbox"
                                    checked={formatGroup.placements[0]?.enabled || false}
                                    onChange={() => {
                                        const placement = formatGroup.placements[0];
                                        const placementId = placement?.id || `${placement?.name}-${placement?.position || 'default'}`;
                                        togglePlacementEnabled(placementId);
                                    }}
                                />
                                <span className="toggle-slider"></span>
                            </label>
                            <span className="toggle-label">
                                {formatGroup.placements[0]?.enabled ? 'Ativo' : 'Inativo'}
                            </span>
                        </div>
                    )}
                </div>

                {hasMultiplePositions && (
                    <div className="format-positions">
                        {formatGroup.placements.map((placement: any) => {
                            const baseId = placement.id || `${placement.name}-${placement.position}`;
                            const isEnabled = placement.enabled;

                            // Mapear posições para labels amigáveis
                            const getPositionLabel = (position: string) => {
                                const positionLabels: any = {
                                    'top': '🔝 Topo da Página',
                                    'bottom': '⬇️ Rodapé da Página',
                                    'left': '⬅️ Lateral Esquerda',
                                    'right': '➡️ Lateral Direita',
                                    'top-left': '↖️ Superior Esquerdo',
                                    'top-right': '↗️ Superior Direito',
                                    'bottom-left': '↙️ Inferior Esquerdo',
                                    'bottom-right': '↘️ Inferior Direito'
                                };
                                return positionLabels[position] || position;
                            };

                            return (
                                <div key={baseId} className={`position-card ${isEnabled ? 'enabled' : 'disabled'}`}>
                                    <div className="position-header">
                                        <div className="position-info">
                                            <h5 className="position-name">
                                                {placement.position ? getPositionLabel(placement.position) : 'Padrão'}
                                            </h5>
                                        </div>

                                        <div className="position-toggle">
                                            <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isEnabled}
                                                    onChange={() => togglePlacementEnabled(baseId)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <span className="toggle-label">
                                                {isEnabled ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </div>
                                    </div>

                                    {isEnabled && renderFormatConfig(placement)}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!hasMultiplePositions && renderFormatConfig(formatGroup.placements[0])}
            </div>
        );
    };

    const adpFormatGroups = getAdpPlacements();
    const customPlacements = getCustomPlacements();

    return (
        <div className="adp-formats-container">
            {/* Formatos Automáticos Predefinidos - Agora em primeiro */}
            <div className="content-section">
                <div className="d-flex justify-content-between align-items-end mb-4 pb-3 border-bottom">
                    <div className="flex-grow-1">
                        <h3 className="section-title mb-2">📱 Formatos Automáticos</h3>
                        <p className="text-muted mb-0 small">
                            Formatos otimizados e pré-configurados para diferentes tipos de conteúdo
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-4 ms-4">
                        <div className="d-flex gap-4">
                            <div className="text-center">
                                <div className="fs-3 fw-bold text-primary lh-1">{adpFormatGroups.length}</div>
                                <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Formatos</small>
                            </div>
                            <div className="text-center">
                                <div className="fs-3 fw-bold text-success lh-1">
                                    {adpFormatGroups.reduce((acc, group) =>
                                        acc + group.placements.filter((p: any) => p.enabled).length, 0
                                    )}
                                </div>
                                <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Ativos</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                            onClick={() => setShowGlobalSettingsModal(true)}
                            title="Configurações Globais da Tag"
                        >
                            <span>⚙️</span>
                            <span>Configurações</span>
                        </button>
                    </div>
                </div>

                <div className="formats-grid">
                    {adpFormatGroups.map((formatGroup: any) => renderFormatWithPositions(formatGroup))}
                </div>
            </div>

            {/* Formatos Customizados */}
            <div className="content-section">
                <div className="d-flex justify-content-between align-items-end mb-4 pb-3 border-bottom">
                    <div className="flex-grow-1">
                        <h3 className="section-title mb-2">🎨 Formatos Customizados</h3>
                        <p className="text-muted mb-0 small">
                            Crie seus próprios formatos com configurações personalizadas
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-4 ms-4">
                        <div className="d-flex gap-4">
                            <div className="text-center">
                                <div className="fs-4 fw-bold text-primary lh-1">{customPlacements.length}</div>
                                <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Customizados</small>
                            </div>
                            <div className="text-center">
                                <div className="fs-4 fw-bold text-success lh-1">
                                    {customPlacements.filter((p: any) => p.enabled).length}
                                </div>
                                <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>Ativos</small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                            onClick={addCustomFormat}
                            title="Adicionar Formato Customizado"
                        >
                            <span>➕</span>
                            <span>Adicionar Formato</span>
                        </button>
                    </div>
                </div>

                {customPlacements.length === 0 ? (
                    <div className="empty-custom-formats">
                        <div className="empty-icon">🎨</div>
                        <h4 className="empty-title">Nenhum formato customizado</h4>
                        <p className="empty-subtitle">
                            Adicione formatos personalizados para atender necessidades específicas do seu site
                        </p>
                        <button type="button" className="btn-add-custom-empty" onClick={addCustomFormat}>
                            ➕ Criar Primeiro Formato Customizado
                        </button>
                    </div>
                ) : (
                    <div className="custom-formats-grid">
                        {customPlacements.map((customFormat: any, index: number) => {
                            const baseId = customFormat.id || `custom-${index}`;

                            return (
                                <div key={baseId} className="custom-format-card">
                                    <div className="custom-format-header">
                                        <div className="custom-format-title-section">
                                            <input
                                                id={`name-${baseId}`}
                                                name={`name-${baseId}`}
                                                type="text"
                                                className="custom-format-name"
                                                value={customFormat.displayName || ''}
                                                placeholder="Nome do formato customizado"
                                                onChange={(e) => updatePlacementField(baseId, 'displayName', e.target.value)}
                                            />
                                        </div>
                                        <div className="custom-format-actions">
                                            <label className="toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={customFormat.enabled || false}
                                                    onChange={() => togglePlacementEnabled(baseId)}
                                                />
                                                <span className="toggle-slider"></span>
                                            </label>
                                            <button
                                                type="button"
                                                className="btn-remove-custom"
                                                onClick={() => removeCustomFormat(baseId)}
                                                title="Remover formato"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </div>

                                    {customFormat.enabled && (
                                        <div className="custom-format-config">
                                            <div className="custom-config-grid">
                                                <div className="col-12 col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label">Ad Unit Path</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={customFormat.adUnitPath || ''}
                                                            placeholder={handleGenerateAdUnitPath('custom')}
                                                            onChange={(e) => updatePlacementField(baseId, 'adUnitPath', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label">Tempo de recarregamento (seg)</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={customFormat.refresh ?? 30}
                                                            onChange={(e) => updatePlacementField(baseId, 'refresh', parseInt(e.target.value) || 0)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label">Seletor CSS</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={customFormat.selector || ''}
                                                            placeholder="Ex: .content, p, article"
                                                            onChange={(e) => updatePlacementField(baseId, 'selector', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <div className="form-group">
                                                        <label className="form-label">Posição de Injeção</label>
                                                        <select
                                                            className="form-control"
                                                            value={customFormat.injectorPosition || 1}
                                                            onChange={(e) => updatePlacementField(baseId, 'injectorPosition', parseInt(e.target.value) || 1)}
                                                        >
                                                            <option value={1}>Antes do elemento</option>
                                                            <option value={2}>Primeiro filho</option>
                                                            <option value={3}>Último filho</option>
                                                            <option value={4}>Depois do elemento</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="custom-advanced-config">
                                                {renderSizesInput(customFormat)}
                                                {renderAttributesInput(customFormat)}
                                                {/* Show bids configuration if prebid powerup is enabled */}
                                                {handleForm.values.powerUps?.find((pu: any) => pu.name === 'prebid')?.enabled && renderBidsInput(customFormat)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Modal de Configurações Globais */}
            <AdpFormatsConfigModal
                isOpen={showGlobalSettingsModal}
                toggle={() => setShowGlobalSettingsModal(!showGlobalSettingsModal)}
                handleForm={handleForm}
            />
        </div>
    );
};

export default AdpFormatsTab;